import React, { useState, useEffect } from 'react';
import { financialYearsAPI } from '../api/client';
import {
  Calendar, Plus, Star, Trash2, Edit3, Check, X, AlertTriangle, RefreshCw,
  ChevronUp, ChevronDown, Search
} from 'lucide-react';
import TablePagination from '../components/TablePagination';

const FY_PAGE_SIZE = 25;

export default function FinancialYears() {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Sorting
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [fyPage, setFyPage] = useState(1);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    financialYear: new Date().getFullYear(),
    yearLabel: '',
    startDate: '',
    endDate: '',
    isCurrent: false,
  });
  const [creating, setCreating] = useState(false);

  // Edit form
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ yearLabel: '', startDate: '', endDate: '' });
  const [saving, setSaving] = useState(false);

  // Confirm actions
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'delete'|'setCurrent', id, label }
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadYears();
  }, []);

  // Auto-dismiss messages
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(null), 6000);
      return () => clearTimeout(t);
    }
  }, [error]);

  const loadYears = async () => {
    try {
      setLoading(true);
      const res = await financialYearsAPI.getAll();
      setYears(res.data.items || []);
    } catch (err) {
      setError('Failed to load financial years');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.yearLabel || !createForm.startDate || !createForm.endDate) {
      setError('Please fill in all fields');
      return;
    }
    try {
      setCreating(true);
      await financialYearsAPI.create(createForm);
      setSuccess('Financial year created successfully');
      setShowCreate(false);
      setCreateForm({ financialYear: new Date().getFullYear(), yearLabel: '', startDate: '', endDate: '', isCurrent: false });
      await loadYears();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create financial year');
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = (fy) => {
    setEditingId(fy.FinancialYearID);
    setEditForm({
      yearLabel: fy.YearLabel,
      startDate: fy.StartDate?.split('T')[0] || '',
      endDate: fy.EndDate?.split('T')[0] || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.yearLabel || !editForm.startDate || !editForm.endDate) {
      setError('Please fill in all fields');
      return;
    }
    try {
      setSaving(true);
      await financialYearsAPI.update(editingId, editForm);
      setSuccess('Financial year updated successfully');
      setEditingId(null);
      await loadYears();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleSetCurrent = async () => {
    if (!confirmAction) return;
    try {
      setActionLoading(true);
      const res = await financialYearsAPI.setCurrent(confirmAction.id);
      setSuccess(res.data.message || 'Current financial year updated');
      setConfirmAction(null);
      await loadYears();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to set current year');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmAction) return;
    try {
      setActionLoading(true);
      const res = await financialYearsAPI.delete(confirmAction.id);
      setSuccess(res.data.message || 'Financial year deleted');
      setConfirmAction(null);
      await loadYears();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp className="w-3 h-3 text-gray-300" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-blue-600" /> : <ChevronDown className="w-3 h-3 text-blue-600" />;
  };

  const sortedYears = [...years].sort((a, b) => {
    if (!sortField) return 0;
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'IsCurrent':
        return dir * ((a.IsCurrent ? 1 : 0) - (b.IsCurrent ? 1 : 0));
      case 'YearLabel':
        return dir * (a.YearLabel || '').localeCompare(b.YearLabel || '');
      case 'FinancialYear':
        return dir * ((a.FinancialYear || 0) - (b.FinancialYear || 0));
      case 'StartDate':
        return dir * (new Date(a.StartDate || 0) - new Date(b.StartDate || 0));
      case 'EndDate':
        return dir * (new Date(a.EndDate || 0) - new Date(b.EndDate || 0));
      default:
        return 0;
    }
  });
  const filteredYears = sortedYears.filter(fy => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      (fy.YearLabel || '').toLowerCase().includes(q) ||
      String(fy.FinancialYear || '').includes(q)
    );
  });
  const pagedYears = filteredYears.slice((fyPage - 1) * FY_PAGE_SIZE, fyPage * FY_PAGE_SIZE);
  React.useEffect(() => { setFyPage(1); }, [searchTerm, sortField, sortDir]);

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Auto-suggest label & dates when year changes
  const updateYearSuggestion = (yearNum) => {
    const y = parseInt(yearNum);
    if (!y || y < 2000) return;
    setCreateForm(prev => ({
      ...prev,
      financialYear: y,
      yearLabel: prev.yearLabel || `${y}-${y + 1}`,
      startDate: prev.startDate || `${y}-03-01`,
      endDate: prev.endDate || `${y + 1}-02-28`,
    }));
  };

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Financial Years</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Manage your company's financial year periods. Set the current year for budgets and reporting.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadYears}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={() => setShowCreate(!showCreate)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Add Financial Year
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
            <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm font-medium text-green-800">{success}</p>
            <button onClick={() => setSuccess(null)} className="ml-auto text-green-400 hover:text-green-600"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Create Form */}
        {showCreate && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Add New Financial Year</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year Number</label>
                  <input
                    type="number"
                    value={createForm.financialYear}
                    onChange={(e) => updateYearSuggestion(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="2000"
                    max="2099"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">e.g. 2026</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                  <input
                    type="text"
                    value={createForm.yearLabel}
                    onChange={(e) => setCreateForm({ ...createForm, yearLabel: e.target.value })}
                    placeholder="e.g. 2026-2027"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={createForm.startDate}
                    onChange={(e) => setCreateForm({ ...createForm, startDate: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={createForm.endDate}
                    onChange={(e) => setCreateForm({ ...createForm, endDate: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={createForm.isCurrent}
                      onChange={(e) => setCreateForm({ ...createForm, isCurrent: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Set as Current</span>
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create Financial Year'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Financial Years Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/60 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <h2 className="text-sm font-semibold text-gray-700">Financial Year Periods</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                The current year is used across budgets, reports, and the finance dashboard.
              </p>
            </div>
            <div className="relative w-full sm:w-56">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search years..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-3">Loading financial years...</p>
            </div>
          ) : years.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto" />
              <p className="text-gray-500 mt-3">No financial years configured</p>
              <p className="text-sm text-gray-400 mt-1">Click "Add Financial Year" to get started.</p>
            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50/40">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('IsCurrent')}><div className="flex items-center gap-1">Status<SortIcon field="IsCurrent" /></div></th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('YearLabel')}><div className="flex items-center gap-1">Label<SortIcon field="YearLabel" /></div></th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('FinancialYear')}><div className="flex items-center gap-1">Year<SortIcon field="FinancialYear" /></div></th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('StartDate')}><div className="flex items-center gap-1">Start Date<SortIcon field="StartDate" /></div></th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('EndDate')}><div className="flex items-center gap-1">End Date<SortIcon field="EndDate" /></div></th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {sortedYears.length === 0 && searchTerm ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No financial years match your search</td></tr>
                  ) : pagedYears.map((fy) => {
                    const isEditing = editingId === fy.FinancialYearID;

                    return (
                      <tr key={fy.FinancialYearID} className={`hover:bg-gray-50/50 ${fy.IsCurrent ? 'bg-blue-50/30' : ''}`}>
                        {/* Status */}
                        <td className="px-5 py-4">
                          {fy.IsCurrent ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                              <Star className="w-3 h-3 fill-current" />
                              Current
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                              Inactive
                            </span>
                          )}
                        </td>

                        {/* Label */}
                        <td className="px-5 py-4">
                          {isEditing ? (
                            <input
                              type="text"
                              value={editForm.yearLabel}
                              onChange={(e) => setEditForm({ ...editForm, yearLabel: e.target.value })}
                              className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          ) : (
                            <span className="text-sm font-semibold text-gray-900">{fy.YearLabel}</span>
                          )}
                        </td>

                        {/* Year Number */}
                        <td className="px-5 py-4 text-sm text-gray-600">
                          {fy.FinancialYear}
                        </td>

                        {/* Start Date */}
                        <td className="px-5 py-4">
                          {isEditing ? (
                            <input
                              type="date"
                              value={editForm.startDate}
                              onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })}
                              className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          ) : (
                            <span className="text-sm text-gray-600">{formatDate(fy.StartDate)}</span>
                          )}
                        </td>

                        {/* End Date */}
                        <td className="px-5 py-4">
                          {isEditing ? (
                            <input
                              type="date"
                              value={editForm.endDate}
                              onChange={(e) => setEditForm({ ...editForm, endDate: e.target.value })}
                              className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          ) : (
                            <span className="text-sm text-gray-600">{formatDate(fy.EndDate)}</span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-5 py-4 text-right">
                          {isEditing ? (
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={handleSaveEdit}
                                disabled={saving}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                              >
                                <Check className="w-3 h-3" />
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                onClick={() => setEditingId(null)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
                              >
                                <X className="w-3 h-3" />
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-2">
                              {!fy.IsCurrent && (
                                <button
                                  onClick={() => setConfirmAction({ type: 'setCurrent', id: fy.FinancialYearID, label: fy.YearLabel })}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"
                                  title="Set as current financial year"
                                >
                                  <Star className="w-3 h-3" />
                                  Set Current
                                </button>
                              )}
                              <button
                                onClick={() => handleEdit(fy)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200"
                                title="Edit"
                              >
                                <Edit3 className="w-3 h-3" />
                                Edit
                              </button>
                              {!fy.IsCurrent && (
                                <button
                                  onClick={() => setConfirmAction({ type: 'delete', id: fy.FinancialYearID, label: fy.YearLabel })}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
                                  title="Delete"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <TablePagination page={fyPage} pageSize={FY_PAGE_SIZE} total={filteredYears.length} onPageChange={setFyPage} />
            </>
          )}
        </div>

        {/* Info Card */}
        <div className="bg-blue-50/60 border border-blue-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">How Financial Years Work</h3>
          <ul className="text-sm text-blue-800 space-y-1.5">
            <li className="flex items-start gap-2">
              <Star className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>The <strong>current</strong> financial year is used by default in the Finance Dashboard, budget management, and reporting filters.</span>
            </li>
            <li className="flex items-start gap-2">
              <Calendar className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>When a new fiscal period begins, click <strong>"Set Current"</strong> on the new year to make it the active period.</span>
            </li>
            <li className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>You cannot delete a year that is set as current. Assign a different year as current first.</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Confirm Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            {confirmAction.type === 'setCurrent' ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Star className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Set Current Financial Year</h3>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Are you sure you want to set <strong>{confirmAction.label}</strong> as the current financial year?
                  This will affect the Finance Dashboard, budget defaults, and all reporting that uses the current year.
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Financial Year</h3>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Are you sure you want to delete <strong>{confirmAction.label}</strong>?
                  This will not delete associated budgets, but they will no longer be linked to this year.
                </p>
              </>
            )}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmAction(null)}
                disabled={actionLoading}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmAction.type === 'setCurrent' ? handleSetCurrent : handleDelete}
                disabled={actionLoading}
                className={`px-4 py-2 text-sm font-medium rounded-lg text-white disabled:opacity-50 ${
                  confirmAction.type === 'setCurrent'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {actionLoading
                  ? 'Processing...'
                  : confirmAction.type === 'setCurrent'
                    ? 'Set as Current'
                    : 'Delete'
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
